'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

const STEPS = [
  { id: 1, label: 'INCIDENT CLASSIFICATION', tag: '[STEP 01]' },
  { id: 2, label: 'TARGET ASSET INTEL', tag: '[STEP 02]' },
  { id: 3, label: 'FORENSIC EVIDENCE (OPTIONAL)', tag: '[STEP 03]' },
  { id: 4, label: 'CONTACT & SUBMIT', tag: '[STEP 04]' },
];

const INCIDENT_TYPES = [
  { id: 'pig-butchering', label: 'Pig Butchering / Romance Scam', tag: 'HIGH VOLUME' },
  { id: 'exchange-hack', label: 'Exchange Account Compromise', tag: 'TECHNICAL' },
  { id: 'rug-pull', label: 'DeFi Rug Pull / Exit Scam', tag: 'ON-CHAIN' },
  { id: 'phishing', label: 'Phishing / Wallet Drain', tag: 'SOCIAL ENG.' },
  { id: 'investment-fraud', label: 'Fake Investment Platform', tag: 'SYNDICATE' },
  { id: 'other', label: 'Other / Unknown Vector', tag: 'CLASSIFY' },
];

const CHAINS = ['Bitcoin (BTC)', 'Ethereum (ETH)', 'Tron (TRC-20)', 'Solana (SOL)', 'BNB Chain', 'Polygon', 'Arbitrum', 'USDT (Tether)', 'Monero (XMR)', 'Wire Transfer', 'Other / Unknown'];

interface FormData {
  incidentType: string;
  estimatedLoss: string;
  incidentDate: string;
  walletAddress: string;
  txHash: string;
  chain: string;
  platformUrl: string;
  files: File[];
  name: string;
  email: string;
  jurisdiction: string;
  additionalContext: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export default function IntakeFormClient() {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [caseRef, setCaseRef] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [todayDate, setTodayDate] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<FormData>({
    incidentType: '',
    estimatedLoss: '',
    incidentDate: '',
    walletAddress: '',
    txHash: '',
    chain: '',
    platformUrl: '',
    files: [],
    name: '',
    email: '',
    jurisdiction: '',
    additionalContext: '',
  });

  const renderTurnstile = useCallback(() => {
    if (!turnstileContainerRef.current || !window.turnstile) return;
    if (turnstileWidgetId) {
      try { window.turnstile.remove(turnstileWidgetId); } catch {}
    }
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA';
    const id = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: siteKey,
      theme: 'dark',
      callback: (token: string) => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(''),
      'error-callback': () => setTurnstileToken(''),
    });
    setTurnstileWidgetId(id);
  }, [turnstileWidgetId]);

  // Load Turnstile script when reaching step 4
  useEffect(() => {
    if (currentStep !== 4) return;

    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    window.onTurnstileLoad = () => renderTurnstile();

    if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    setTodayDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFormData((prev) => ({ ...prev, files: [...prev.files, ...dropped] }));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      setFormData((prev) => ({ ...prev, files: [...prev.files, ...selected] }));
    }
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }));
  };

  const canProceed = () => {
    if (currentStep === 1) return formData.incidentType !== '';
    if (currentStep === 2) return formData.chain !== '';
    if (currentStep === 3) return true;
    if (currentStep === 4) return formData.name !== '' && formData.email !== '' && turnstileToken !== '';
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canProceed() || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Base64-encode all uploaded files
      const attachments = await Promise.all(
        formData.files.map(async (file) => {
          const arrayBuffer = await file.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64 = btoa(binary);
          return {
            filename: file.name,
            content: base64,
            contentType: file.type || 'application/octet-stream',
          };
        })
      );

      const res = await fetch('/api/triage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          turnstileToken,
          name: formData.name,
          email: formData.email,
          incidentType: formData.incidentType,
          estimatedLoss: formData.estimatedLoss,
          incidentDate: formData.incidentDate,
          walletAddress: formData.walletAddress,
          txHash: formData.txHash,
          chain: formData.chain,
          platformUrl: formData.platformUrl,
          jurisdiction: formData.jurisdiction,
          additionalContext: formData.additionalContext,
          attachments,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setSubmitError(data.error ?? 'Submission failed. Please try again.');
        if (turnstileWidgetId && window.turnstile) {
          window.turnstile.reset(turnstileWidgetId);
        }
        setTurnstileToken('');
        return;
      }

      setCaseRef(data.caseId);
      setSubmitted(true);
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-lg w-full glass-card border border-border p-8 md:p-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 border border-accent flex items-center justify-center">
              <Icon name="CheckIcon" size={32} className="text-accent" />
            </div>
          </div>
          <div className="bracket-tag text-accent mb-4">[CASE INGESTION SECURE // FILE REFERENCE ASSIGNED]</div>
          <h1 className="text-2xl font-black tracking-tighter uppercase text-foreground mb-4">
            Triage Initiated
          </h1>
          <p className="text-muted-foreground font-light leading-relaxed mb-6">
            Your incident has been securely logged and assigned to an intelligence analyst.
            Check your encrypted communication channel for tactical receipt.
          </p>
          <div className="border border-border p-4 mb-4">
            <div className="bracket-tag text-muted-foreground mb-1">CASE FILE REFERENCE</div>
            <div className="text-xl font-black terminal-text text-foreground">
              #{caseRef}
            </div>
          </div>
          <div className="border border-border p-3 mb-6">
            <p className="text-xs text-muted-foreground font-light leading-relaxed">
              A secure transmission receipt has been dispatched to your encrypted communication channel.
              Expected analyst response: <span className="text-foreground font-medium">4 hours</span>.
            </p>
          </div>
          <p className="bracket-tag text-muted-foreground">[DO NOT DISCARD THIS REFERENCE]</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-20 px-4" aria-label="ForensicDesk secure intake form">
      {/* Ambient */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        <div
          className="absolute top-[10%] right-[-5%] w-64 h-64 rounded-full"
          style={{ background: 'rgba(220,38,38,0.05)', filter: 'blur(80px)' }}
        />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="bracket-tag text-primary">[SECURE INTAKE // ENCRYPTED CHANNEL]</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-foreground leading-tight mb-3">
            Incident Triage<br />
            <span className="text-gradient-crimson">Intake Form</span>
          </h1>
          <p className="text-muted-foreground font-light">
            All submissions are encrypted. This intake initiates a formal intelligence assessment.
            Complete all four steps to open a case file.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 mb-10 overflow-x-auto pb-2" role="tablist" aria-label="Form steps">
          {STEPS.map((step, i) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            return (
              <React.Fragment key={step.id}>
                <div
                  className="flex flex-col items-center gap-1 shrink-0"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={step.label}
                >
                  <div
                    className={`w-8 h-8 flex items-center justify-center border text-xs font-black transition-all duration-300 ${
                      isCompleted ? 'step-completed' : isActive ? 'step-active' : 'step-inactive'
                    }`}
                  >
                    {isCompleted ? <Icon name="CheckIcon" size={14} /> : step.id}
                  </div>
                  <span className="bracket-tag text-muted-foreground hidden sm:block text-center leading-tight max-w-[80px]">
                    {step.tag}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-px mx-2 transition-all duration-500 ${
                      step.id < currentStep ? 'bg-accent' : 'bg-border'
                    }`}
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="glass-card border border-border p-6 md:p-8 relative">
            {/* Step scan line */}
            <div className="absolute top-0 left-0 w-full h-px border-scan" aria-hidden="true" />

            {/* Step header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
              <span className="bracket-tag text-primary">{STEPS[currentStep - 1].tag}</span>
              <span className="text-sm font-black uppercase tracking-wider text-foreground">
                {STEPS[currentStep - 1].label}
              </span>
            </div>

            {/* STEP 1: Incident Classification */}
            {currentStep === 1 && (
              <fieldset className="space-y-4">
                <legend className="text-sm text-muted-foreground font-light mb-6">
                  Select the incident classification that best describes your situation.
                </legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {INCIDENT_TYPES.map((type) => (
                    <label
                      key={type.id}
                      className={`flex items-start gap-3 p-4 border cursor-pointer transition-all duration-200 group ${
                        formData.incidentType === type.id
                          ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <input
                        type="radio"
                        name="incidentType"
                        value={type.id}
                        checked={formData.incidentType === type.id}
                        onChange={() => updateField('incidentType', type.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                          formData.incidentType === type.id ? 'border-primary' : 'border-border'
                        }`}
                      >
                        {formData.incidentType === type.id && (
                          <div className="w-2 h-2 bg-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground leading-tight">{type.label}</div>
                        <div className="bracket-tag text-muted-foreground mt-1">[{type.tag}]</div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div>
                    <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="estimatedLoss">
                      ESTIMATED LOSS (USD)
                    </label>
                    <input
                      id="estimatedLoss"
                      type="text"
                      value={formData.estimatedLoss}
                      onChange={(e) => updateField('estimatedLoss', e.target.value)}
                      placeholder="e.g. 250000"
                      className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors terminal-text"
                      suppressHydrationWarning
                    />
                  </div>
                  <div>
                    <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="incidentDate">
                      INCIDENT DATE
                    </label>
                    <input
                      id="incidentDate"
                      type="date"
                      value={formData.incidentDate}
                      max={todayDate}
                      onChange={(e) => updateField('incidentDate', e.target.value)}
                      className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground outline-none transition-colors terminal-text date-input-white-icon"
                      suppressHydrationWarning
                    />
                  </div>
                </div>
              </fieldset>
            )}

            {/* STEP 2: Target Asset Intelligence */}
            {currentStep === 2 && (
              <fieldset className="space-y-5">
                <legend className="text-sm text-muted-foreground font-light mb-6">
                  Provide all known on-chain identifiers. Partial information is acceptable.
                </legend>

                <div>
                  <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="walletAddress">
                    SUSPECT WALLET ADDRESS
                  </label>
                  <input
                    id="walletAddress"
                    type="text"
                    value={formData.walletAddress}
                    onChange={(e) => updateField('walletAddress', e.target.value)}
                    placeholder="0x... or bc1... or T..."
                    className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors terminal-text"
                    suppressHydrationWarning
                  />
                </div>

                <div>
                  <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="txHash">
                    TRANSACTION HASH (TX ID)
                  </label>
                  <input
                    id="txHash"
                    type="text"
                    value={formData.txHash}
                    onChange={(e) => updateField('txHash', e.target.value)}
                    placeholder="0x... or full TXID"
                    className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors terminal-text"
                    suppressHydrationWarning
                  />
                </div>

                <div>
                  <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="chain">
                    BLOCKCHAIN NETWORK <span className="text-primary">*</span>
                  </label>
                  <select
                    id="chain"
                    value={formData.chain}
                    onChange={(e) => updateField('chain', e.target.value)}
                    required
                    className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground outline-none transition-colors terminal-text appearance-none cursor-pointer"
                  >
                    <option value="" disabled>Select network...</option>
                    {CHAINS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="platformUrl">
                    FRAUD PLATFORM / DOMAIN URL
                  </label>
                  <input
                    id="platformUrl"
                    type="text"
                    value={formData.platformUrl}
                    onChange={(e) => updateField('platformUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors terminal-text"
                  />
                </div>
              </fieldset>
            )}

            {/* STEP 3: Forensic Evidence Upload */}
            {currentStep === 3 && (
              <fieldset className="space-y-5">
                <legend className="text-sm text-muted-foreground font-light mb-6">
                  Upload any supporting evidence: screenshots, email headers, transaction records, or chat logs. <span className="text-muted-foreground/60">(Optional — you may skip this step)</span>
                </legend>

                <div
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed p-8 md:p-12 text-center cursor-pointer transition-all duration-300 ${
                    dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground'
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload forensic evidence files"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                >
                  <Icon name="CloudArrowUpIcon" size={32} className="text-muted-foreground mx-auto mb-3" />
                  <div className="text-sm font-medium text-foreground mb-1">
                    Drag & drop files here, or click to browse
                  </div>
                  <div className="bracket-tag text-muted-foreground">
                    [PNG / JPG / PDF / EML / CSV // MAX 50MB PER FILE]
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={handleFileInput}
                    accept=".png,.jpg,.jpeg,.pdf,.eml,.csv,.txt"
                    aria-label="File upload input"
                  />
                </div>

                {formData.files.length > 0 && (
                  <div className="space-y-2">
                    <div className="bracket-tag text-muted-foreground mb-3">
                      [{formData.files.length} FILE{formData.files.length !== 1 ? 'S' : ''} STAGED FOR UPLOAD]
                    </div>
                    {formData.files.map((file, i) => (
                      <div key={`${file.name}-${i}`} className="flex items-center justify-between gap-3 p-3 border border-border bg-card">
                        <div className="flex items-center gap-3 min-w-0">
                          <Icon name="DocumentIcon" size={14} className="text-muted-foreground shrink-0" />
                          <span className="text-sm text-foreground truncate terminal-text">{file.name}</span>
                          <span className="bracket-tag text-muted-foreground shrink-0">
                            {(file.size / 1024 / 1024).toFixed(1)}MB
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="text-muted-foreground hover:text-primary transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                          aria-label={`Remove ${file.name}`}
                        >
                          <Icon name="XMarkIcon" size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border border-border p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="LockClosedIcon" size={14} className="text-accent shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground font-light leading-relaxed">
                      All uploaded files are encrypted in transit and at rest using AES-256.
                      Files are accessible only to assigned ForensicDesk analysts and are purged
                      after case closure unless otherwise instructed.
                    </p>
                  </div>
                </div>
              </fieldset>
            )}

            {/* STEP 4: Contact & Submit */}
            {currentStep === 4 && (
              <fieldset className="space-y-5">
                <legend className="text-sm text-muted-foreground font-light mb-6">
                  Provide secure contact details. We will reach you via encrypted channel.
                </legend>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="name">
                      FULL NAME / ALIAS <span className="text-primary">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      required
                      placeholder="Name or secure alias"
                      className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                      suppressHydrationWarning
                    />
                  </div>
                  <div>
                    <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="email">
                      SECURE EMAIL <span className="text-primary">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      required
                      placeholder="your@email.com"
                      className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors terminal-text"
                      suppressHydrationWarning
                    />
                  </div>
                </div>

                <div>
                  <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="jurisdiction">
                    JURISDICTION / COUNTRY
                  </label>
                  <input
                    id="jurisdiction"
                    type="text"
                    value={formData.jurisdiction}
                    onChange={(e) => updateField('jurisdiction', e.target.value)}
                    placeholder="e.g. United States, United Kingdom"
                    className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors"
                    suppressHydrationWarning
                  />
                </div>

                <div>
                  <label className="bracket-tag text-muted-foreground block mb-2" htmlFor="additionalContext">
                    ADDITIONAL CONTEXT
                  </label>
                  <textarea
                    id="additionalContext"
                    value={formData.additionalContext}
                    onChange={(e) => updateField('additionalContext', e.target.value)}
                    rows={4}
                    placeholder="Brief description of the incident, timeline, or any additional intelligence you can provide..."
                    className="w-full bg-input border border-border focus:border-primary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors resize-none font-light leading-relaxed"
                  />
                </div>

                <div className="border border-border p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      required
                      className="mt-1 shrink-0 accent-red-600 w-4 h-4"
                      suppressHydrationWarning
                    />
                    <label htmlFor="consent" className="text-xs text-muted-foreground font-light leading-relaxed cursor-pointer">
                      I confirm the information provided is accurate to the best of my knowledge.
                      I consent to ForensicDesk processing this data for the purpose of intelligence
                      analysis and case management in accordance with their operational protocols.
                    </label>
                  </div>
                </div>

                {/* Cloudflare Turnstile Widget */}
                <div className="border border-border p-4">
                  <div className="bracket-tag text-muted-foreground mb-3">[SECURITY VERIFICATION REQUIRED]</div>
                  <div ref={turnstileContainerRef} className="cf-turnstile" />
                  {!turnstileToken && (
                    <p className="text-xs text-muted-foreground mt-2 font-light">
                      Complete the security challenge above to enable submission.
                    </p>
                  )}
                  {turnstileToken && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                      <span className="bracket-tag text-accent text-xs">VERIFICATION COMPLETE</span>
                    </div>
                  )}
                </div>

                {/* Submit error */}
                {submitError && (
                  <div className="border border-primary p-4">
                    <div className="flex items-start gap-3">
                      <Icon name="ExclamationTriangleIcon" size={14} className="text-primary shrink-0 mt-0.5" />
                      <p className="text-xs text-primary font-light leading-relaxed">{submitError}</p>
                    </div>
                  </div>
                )}
              </fieldset>
            )}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-border">
              <button
                type="button"
                onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
                disabled={currentStep === 1}
                className="flex items-center gap-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground px-5 py-3 bracket-tag transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed min-h-[44px]"
              >
                <Icon name="ArrowLeftIcon" size={14} />
                BACK
              </button>

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep((s) => Math.min(4, s + 1))}
                  disabled={!canProceed()}
                  className="shine-effect flex items-center gap-2 bg-primary hover:bg-red-700 disabled:bg-primary/40 text-primary-foreground px-6 py-3 bracket-tag transition-colors duration-200 disabled:cursor-not-allowed min-h-[44px]"
                >
                  PROCEED
                  <Icon name="ArrowRightIcon" size={14} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!canProceed() || isSubmitting}
                  className="shine-effect flex items-center gap-2 bg-primary hover:bg-red-700 disabled:bg-primary/40 text-primary-foreground px-8 py-3 bracket-tag transition-colors duration-200 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground animate-pulse" />
                      TRANSMITTING...
                    </>
                  ) : (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground threat-pulse" />
                      INITIATE TRIAGE
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Footer note */}
        <div className="mt-6 flex items-center gap-3 justify-center">
          <span className="w-1 h-1 bg-accent" />
          <p className="bracket-tag text-muted-foreground text-center">
            [CASE OPENED WITHIN 4 HOURS // ENCRYPTED // ATTORNEY-CLIENT PRIVILEGE AVAILABLE]
          </p>
          <span className="w-1 h-1 bg-accent" />
        </div>
      </div>
    </section>
  );
}